import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Send, Plus, Trash2, Users, ListChecks, Instagram, ExternalLink,
  CheckCircle2, Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};
const fmtInr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

const STATUS_META = {
  draft:               { label: 'Draft',                 color: '#71717a' },
  pending_brand_review:{ label: 'Pending Brand Review',  color: '#FFB800' },
  finalized:           { label: 'Finalized',             color: '#0055FF' },
  outreach_started:    { label: 'Outreach Started',      color: '#00C972' },
};

// Campaign Manager view of a single review list.
// Handles: edit name/notes (draft), add/remove creators, submit-to-brand, view brand selections after submission, start outreach after finalize.
export const CMReviewListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rl, setRl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [shortlists, setShortlists] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: sls }] = await Promise.all([
        axios.get(`${API_URL}/api/review-lists/${id}`, { withCredentials: true }),
        // For "Bulk add" we let the CM import from their assigned brand's shortlists.
        // It's the most natural source; if that 403s for CMs we silently ignore.
        axios.get(`${API_URL}/api/shortlists`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setRl(data);
      setShortlists(Array.isArray(sls) ? sls : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load review list');
      navigate('/cm/review-lists');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isDraft = rl?.status === 'draft';
  const isFinalized = rl?.status === 'finalized';
  const isPending = rl?.status === 'pending_brand_review';
  const isOutreachStarted = rl?.status === 'outreach_started';

  const meta = STATUS_META[rl?.status] || STATUS_META.draft;

  const removeCreator = async (creatorId) => {
    if (!window.confirm('Remove this creator from the review list?')) return;
    try {
      await axios.delete(`${API_URL}/api/review-lists/${id}/creators/${creatorId}`, { withCredentials: true });
      await load(); toast.success('Removed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Remove failed'); }
  };

  const submitToBrand = async () => {
    if (!window.confirm('Submit this list to the brand for review? You won\'t be able to edit creators after this.')) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/review-lists/${id}/submit`, {}, { withCredentials: true });
      toast.success('Submitted to brand');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  const startOutreach = async () => {
    if (!window.confirm('Create bulk-campaign rows for all shortlisted creators?')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/review-lists/${id}/start-outreach`, {}, { withCredentials: true });
      toast.success(`Outreach started — ${data.inserted} new, ${data.skipped} already in campaign`);
      navigate(`/bulk/campaigns/${rl.campaign_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  if (loading) return <FullPageSpinner />;
  if (!rl) return null;

  const summary = rl.summary || {};

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="cm-review-list-detail-page">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => navigate('/cm/review-lists')} className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6" data-testid="cm-back-button">
            <ArrowLeft size={14} /> All review lists
          </button>

          <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
            <div className="min-w-0">
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="cm-review-list-title">{rl.name}</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400 text-sm">{rl.campaign_name} · {rl.brand_name}</p>
              <span className="inline-flex mt-2 text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}
                data-testid="cm-review-list-status">
                {meta.label}
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isDraft && (
                <Button onClick={() => setAddOpen(true)} className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-4 py-2 text-sm inline-flex items-center gap-1.5" data-testid="cm-add-creators-button">
                  <Plus size={13} /> Add Creators
                </Button>
              )}
              {isDraft && (rl.creator_ids?.length || 0) > 0 && (
                <Button onClick={submitToBrand} disabled={submitting} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)] flex items-center gap-1.5 disabled:opacity-40" data-testid="cm-submit-to-brand-button">
                  {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Submit to Brand
                </Button>
              )}
              {isFinalized && (
                <Button onClick={startOutreach} disabled={busy} className="bg-[#00C972] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-[#00B863] flex items-center gap-1.5 disabled:opacity-40" data-testid="cm-start-outreach-button">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />} Start Outreach
                </Button>
              )}
              {isOutreachStarted && (
                <Link to={`/bulk/campaigns/${rl.campaign_id}`} className="bg-[#00C972]/10 border border-[#00C972]/30 text-[#00C972] rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5">
                  <CheckCircle2 size={13} /> Outreach Started · Open Workspace
                </Link>
              )}
            </div>
          </div>

          {/* Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="cm-summary-tiles">
            <Tile label="Total Submitted" value={summary.total ?? 0} color="#71717a" testid="cm-summary-total" />
            <Tile label="Shortlisted"     value={summary.shortlisted ?? 0} color="#00C972" testid="cm-summary-shortlisted" />
            <Tile label="Rejected"        value={summary.rejected ?? 0} color="#FF3B30" testid="cm-summary-rejected" />
            <Tile label="Estimated Reach" value={fmtNum(summary.total_reach)} color="#0055FF" testid="cm-summary-reach" />
          </div>

          {/* Categories */}
          {(summary.categories_represented || []).length > 0 && (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-4 mb-6">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2 font-mono">Categories represented</div>
              <div className="flex flex-wrap gap-1">
                {summary.categories_represented.map((cat) => (
                  <span key={cat} className="px-2 py-0.5 text-[11px] rounded-full bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/20 font-medium uppercase tracking-wider">{cat}</span>
                ))}
              </div>
            </div>
          )}

          {/* Creator rows */}
          <div className="space-y-2" data-testid="cm-creator-rows">
            {(rl.creators || []).length === 0 ? (
              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center">
                <Users className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={40} />
                <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">No creators yet. Click "Add Creators" to start.</p>
              </div>
            ) : (rl.creators || []).map((c) => {
              const igUrl = c.instagram_handle ? `https://instagram.com/${c.instagram_handle.replace(/^@/, '')}` : null;
              return (
                <div key={c.id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid="cm-creator-row">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-sm font-bold shrink-0">
                    {c.name?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
                    {c.instagram_handle && (
                      <a href={igUrl} target="_blank" rel="noreferrer" className="text-[11px] text-[#6B6B6B] dark:text-zinc-400 hover:text-[#E1306C] inline-flex items-center gap-1">
                        <Instagram size={10} /> @{c.instagram_handle} · {fmtNum(c.instagram_followers)}
                      </a>
                    )}
                  </div>
                  <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-400 font-mono">{fmtInr(c.pricing_reel)}</div>
                  {/* Brand decision pill — only shown post-submission */}
                  {!isDraft && (
                    <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: c.decision === 'shortlisted' ? '#00C97215' : c.decision === 'rejected' ? '#FF3B3015' : '#71717a15',
                        color: c.decision === 'shortlisted' ? '#00C972' : c.decision === 'rejected' ? '#FF3B30' : '#71717a',
                        border: `1px solid ${c.decision === 'shortlisted' ? '#00C97230' : c.decision === 'rejected' ? '#FF3B3030' : '#71717a30'}`,
                      }}>
                      {c.decision || 'undecided'}
                    </span>
                  )}
                  {isDraft && (
                    <button onClick={() => removeCreator(c.id)} className="text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] p-1 transition-colors" data-testid="cm-remove-creator-button">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add creators modal */}
      <AddCreatorsDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={async () => { setAddOpen(false); await load(); }}
        listId={id}
        shortlists={shortlists}
      />
    </div>
  );
};

const Tile = ({ label, value, color, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">{label}</div>
    <div className="text-xl font-bold font-mono" style={{ color }}>{value}</div>
  </div>
);

const FullPageSpinner = () => (
  <div className="flex min-h-screen">
    <Sidebar />
    <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
      <Loader2 className="animate-spin text-[#FF451A]" size={32} />
    </div>
  </div>
);

// Add Creators modal — two paths: bulk-import from an existing brand shortlist, or paste IDs.
const AddCreatorsDialog = ({ open, onClose, onAdded, listId, shortlists }) => {
  const [tab, setTab] = useState('shortlist'); // 'shortlist' | 'ids'
  const [pickedShortlist, setPickedShortlist] = useState('');
  const [idsText, setIdsText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (creatorIds) => {
    if (!creatorIds.length) { toast.error('Add at least one creator'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/review-lists/${listId}/creators`, { creator_ids: creatorIds }, { withCredentials: true });
      toast.success(`Added ${data.inserted} creator${data.inserted === 1 ? '' : 's'} (${data.skipped} skipped)`);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  const submitShortlist = async () => {
    if (!pickedShortlist) { toast.error('Pick a shortlist'); return; }
    setBusy(true);
    try {
      const { data: sl } = await axios.get(`${API_URL}/api/shortlists/${pickedShortlist}`, { withCredentials: true });
      const ids = (sl.creator_ids || []);
      await submit(ids);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to read shortlist');
      setBusy(false);
    }
  };

  const submitIds = async () => {
    const ids = idsText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    await submit(ids);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add creators</DialogTitle>
          <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
            Bulk-add from a saved shortlist, or paste creator IDs manually.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 mb-3 border-b border-black/[0.08] dark:border-white/5">
          {[
            { k: 'shortlist', label: 'From Shortlist' },
            { k: 'ids', label: 'Paste IDs' },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? 'border-[#FF451A] text-[#0F0F0F] dark:text-white' : 'border-transparent text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
              data-testid={`add-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'shortlist' && (
          <div className="space-y-3">
            <select value={pickedShortlist} onChange={(e) => setPickedShortlist(e.target.value)}
              className="w-full rounded-xl text-sm p-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="add-shortlist-select">
              <option value="">Choose a brand shortlist…</option>
              {shortlists.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.creator_count} creators)</option>
              ))}
            </select>
            {shortlists.length === 0 && (
              <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500">No shortlists available — try the "Paste IDs" tab.</div>
            )}
            <Button onClick={submitShortlist} disabled={busy} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] disabled:opacity-40" data-testid="add-shortlist-submit">
              {busy ? 'Adding…' : 'Add from shortlist'}
            </Button>
          </div>
        )}
        {tab === 'ids' && (
          <div className="space-y-3">
            <textarea value={idsText} onChange={(e) => setIdsText(e.target.value)} rows={5}
              placeholder="Paste creator IDs separated by commas, spaces or newlines"
              className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 font-mono bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="add-ids-input" />
            <Button onClick={submitIds} disabled={busy} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] disabled:opacity-40" data-testid="add-ids-submit">
              {busy ? 'Adding…' : 'Add by ID'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
